// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { formId } from "@conklin/Overrides/Widgets/ContactUs/ContactForm";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    zipCode: state.components.contactUsForm.fieldValues["zipCode"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ZipCodeStyles {
    zipCodeTextField?: TextFieldPresentationProps;
}

export const zipCodeStyles: ZipCodeStyles = {};

const styles = zipCodeStyles;

const ZipCode: React.FC<Props> = ({ fields, zipCode, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [zipCodeErrorMessage, setZipCodeErrorMessage] = React.useState("");

    React.useEffect(() => {
        contactFormContext.validators.zipCode = validateZipCode;
        if (typeof zipCode !== "undefined") {
            validateZipCode();
        }
    }, [zipCode]);

    React.useEffect(() => {
        contactFormContext.validators.zipCode = validateZipCode;
    }, [isRequired]);

    const validateZipCode = () => {
        const errorMessage = isRequired && !zipCode ? (siteMessage("Field_Required", label) as string) : "";
        setZipCodeErrorMessage(errorMessage);
        return !errorMessage;
    };

    const zipCodeChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "zipCode", value: event.target.value });
    };

    return (
        <TextField
            {...styles.zipCodeTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={zipCode || ""}
            error={zipCodeErrorMessage}
            onChange={zipCodeChangeHandler}
            type="postalCodeUS"
            {...{ formId }}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ZipCode),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "ZIP Code",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Enter Zip Code",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
